import { IonIcon } from '@ionic/react';
import type { ComponentProps } from 'react';
import { createOutline, trashOutline, returnUpBackOutline, flag } from 'ionicons/icons';
import { MarkdownContent } from '@/components/markdown';
import type { GetLeagueMessagesQuery } from '../../../../types/graphql-generated';
import { UserAvatar } from '../../../components/UserAvatar';

type LeagueMessage = GetLeagueMessagesQuery['leagueMessages'][0];
type IconType = ComponentProps<typeof IonIcon>['icon'];

interface LeagueMessageCardProps {
  message: LeagueMessage;
  messagesLastReadAt?: string | null;
  authorTeamName?: string | null;
  /** Only the author of a message may edit or delete it. */
  canManage?: boolean;
  /** Messages written by other users can be replied to. */
  canReply?: boolean;
  /** Messages written by other users can be reported as inappropriate. */
  canReport?: boolean;
  onEdit?: (message: LeagueMessage) => void;
  onDelete?: (message: LeagueMessage) => void;
  onReply?: (message: LeagueMessage) => void;
  onReport?: (message: LeagueMessage) => void;
  onAuthorClick?: () => void;
}

const ADMIN_BADGE_LABEL = 'Futyclub';

const madridDateKey = (date: Date) =>
  date.toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });

// "hoy · 10:59", "ayer · 22:45", "16 sept · 18:53" — the date lives next to the
// time inside the message, so the timeline needs no day-group headers.
const formatDateTime = (dateTime: string) => {
  const date = new Date(dateTime);
  const time = date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Madrid',
  });

  const now = new Date();
  const msgKey = madridDateKey(date);
  const todayKey = madridDateKey(now);
  const yesterdayKey = madridDateKey(new Date(now.getTime() - 24 * 60 * 60 * 1000));

  let dayLabel: string;
  if (msgKey === todayKey) {
    dayLabel = 'hoy';
  } else if (msgKey === yesterdayKey) {
    dayLabel = 'ayer';
  } else {
    dayLabel = date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      timeZone: 'Europe/Madrid',
    });
  }

  return `${dayLabel} · ${time}`;
};

interface ActionButtonProps {
  icon: IconType;
  label: string;
  ariaLabel: string;
  danger?: boolean;
  onClick: () => void;
}

const ActionButton = ({ icon, label, ariaLabel, danger = false, onClick }: ActionButtonProps) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 text-xs font-bold transition-colors ${
      danger ? 'text-red-600 hover:text-red-700' : 'text-slate-500 hover:text-slate-700'
    }`}
  >
    <IonIcon icon={icon} className="text-sm" />
    {label}
  </button>
);

export const LeagueMessageCard = ({
  message,
  messagesLastReadAt,
  authorTeamName,
  canManage = false,
  canReply = false,
  canReport = false,
  onEdit,
  onDelete,
  onReply,
  onReport,
  onAuthorClick,
}: LeagueMessageCardProps) => {
  const isAdminMessage = !message.user;
  const authorName = isAdminMessage
    ? ADMIN_BADGE_LABEL
    : authorTeamName || message.user?.fullName || 'Usuario desconocido';
  const isUnread =
    !messagesLastReadAt || new Date(message.createdAt) > new Date(messagesLastReadAt);

  return (
    <div className="flex items-start gap-3 border-b border-[#e4e8f0] py-3.5 last:border-b-0">
      {onAuthorClick ? (
        <button
          type="button"
          onClick={onAuthorClick}
          aria-label={`Ver plantilla de ${authorName}`}
          className="shrink-0 self-start rounded-full"
        >
          <UserAvatar
            picture={message.user?.picture}
            updatedAt={message.user?.updatedAt}
            name={authorName}
            size={38}
          />
        </button>
      ) : (
        <UserAvatar
          picture={message.user?.picture}
          updatedAt={message.user?.updatedAt}
          name={authorName}
          size={38}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {isUnread && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
          {onAuthorClick ? (
            <button
              type="button"
              onClick={onAuthorClick}
              className="min-w-0 truncate font-heading text-sm font-bold text-slate-800 transition-colors hover:text-[var(--fc-green)]"
            >
              {authorName}
            </button>
          ) : (
            <span className="min-w-0 truncate font-heading text-sm font-bold text-slate-800">
              {authorName}
            </span>
          )}
          <span className="ml-auto shrink-0 whitespace-nowrap text-xs font-medium text-slate-400">
            {formatDateTime(message.createdAt)}
          </span>
        </div>

        <div className="mt-1 text-gray-700">
          <MarkdownContent content={message.text} />
        </div>

        {(canReply || canManage || canReport) && (
          <div className="mt-2 flex items-center gap-4">
            {canReply && (
              <ActionButton
                icon={returnUpBackOutline}
                label="Responder"
                ariaLabel="Responder mensaje"
                onClick={() => onReply?.(message)}
              />
            )}
            {canManage && (
              <>
                <ActionButton
                  icon={createOutline}
                  label="Editar"
                  ariaLabel="Editar mensaje"
                  onClick={() => onEdit?.(message)}
                />
                <ActionButton
                  icon={trashOutline}
                  label="Eliminar"
                  ariaLabel="Eliminar mensaje"
                  danger
                  onClick={() => onDelete?.(message)}
                />
              </>
            )}
            {canReport && (
              <button
                type="button"
                aria-label="Reportar mensaje"
                onClick={() => onReport?.(message)}
                className="ml-auto inline-flex items-center text-red-600 transition-colors hover:text-red-700"
              >
                <IonIcon icon={flag} className="text-base" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
